import { ExportableImage } from 'app/components/Dropzone/ExportStudyModal/exportStudy';
import { SelectableImage } from 'app/components/ImagesSelector';
import { DataImage } from 'app/interfaces/DataImage';
import { DicomData } from 'app/interfaces/Dicom';
import {
  AcquiredDetectorInfo,
  AcquisitionConstants,
  DetectorInfo,
  DisplayableImageData,
  Feedbacks,
  ImageAnnotations,
  ImageDisplayableMetadata,
  ImageMetadata,
  ImageOrigin,
  StoredImageInfo,
  Viewport,
} from 'app/interfaces/Image';
import { ToolsStates } from 'app/adapters/ImageRenderer/ConfigurableToolsOptions';
import { Patient } from 'app/interfaces/Patient';
import { AllPredictions } from 'app/interfaces/Predictions';
import { AcquisitionDetails } from 'app/interfaces/StudyStore';
import { PACSError } from 'app/interfaces/PACSCommunication';
import { Study } from 'app/interfaces/Study';
import { FilterDescription } from 'app/components/Viewer/components/RightPanel/FilterParametrization';

const viewerModes = ['acquisition'] as const;

export type ViewerMode = typeof viewerModes[number];

export const isViewerMode = (s: any): s is ViewerMode => viewerModes.includes(s);

export type ComputeDicomOptions = {
  includeAnnotations?: boolean;
};

export type CompatDropzoneImage = ExportableImage &
  SelectableImage & {
    backendId: string;
    annotations: ImageAnnotations;
    predictions: AllPredictions;
    origin: string;
    computeDicomData?: (options?: ComputeDicomOptions) => Promise<DicomData>;
    dicomData?: DicomData;
  };

export type CompatDropzoneStudy = {
  ID: string;
  animal?: Patient;
  images: CompatDropzoneImage[];
  comment: string;
  isCommentDirty: boolean;
  creationDate: Date;
};

export type RawImageSaveData = {
  dataImage?: DataImage;
  acquisitionDetails?: AcquisitionDetails;
  dicomData?: DicomData;
};

export type ImageSaveState = {
  acquisitionInfo?: {
    anatomicRegion?: string;
    image_metadata?: ImageMetadata;
    acquisitionConstants?: AcquisitionConstants;
    operators?: string[];
    detectorInfo?: DetectorInfo;
    acquisitionTime?: Date;
    feedback?: Feedbacks;
  };
  viewer?: {
    annotations?: ImageAnnotations;
    viewport?: Viewport;
  };
  rawImage?: RawImageSaveData;
};

export type ViewerImage = Partial<StoredImageInfo> & {
  _id: string;
  origin: ImageOrigin;
  anatomicRegion: string;

  // Below is everything that is viewer component specific.
  filename?: string;
  initialDataImage?: DataImage & { rowPixelSpacing: number; columnPixelSpacing: number };
  isNotProcessable?: boolean;
  isSelected?: boolean;
  isImageLoading?: boolean;
  isFromAnotherStudy?: boolean;
  displayableImage?: DisplayableImageData;
  loadError?: boolean;
  dicomData?: DicomData;
  isProcessingOngoing?: boolean;
  processedImage?: DataImage;
  isFromLastProcessing?: boolean;
  isImageNewPredictionsNeeded?: boolean;
  isPredictionsLoading?: boolean;
  inferenceError?: object;
  activeRegionName?: string;
  toolsList?: ToolsStates;
  isAnnotationsSaved?: boolean;
  negatoView?: number;
  pendingAnnotations?: ImageAnnotations;
  isRealSizeMeasurementCalibration?: boolean;
  isFreeRotationActive?: boolean;
  saveState: ImageSaveState;
  reloadedState?: ImageSaveState;
  isRawSavedOnce?: boolean;
  isNewImage?: boolean;
  needRawSave?: boolean;
  displayedMetadata?: ImageDisplayableMetadata;
  isInitialAnnotations?: boolean;
  shownAnnotations?: { [tool: string]: { state: string } };
  realSizeMeasurementHiddenAnnotations?: { [tool: string]: { state: string } };
  disabledTools?: string[];
  photometric_interpretation?: string;
  PACS?: { isSyncOngoing?: boolean; syncError?: PACSError | Error | boolean };

  isGeneratorErrorDismissed?: boolean;

  isFilterFineTuning?: boolean;
  filterParameters?: FilterDescription;
  fineTunedProcessedImage?: DisplayableImageData;
  fineTunedViewport?: Viewport;
  isFineTunedProcessingOngoing?: boolean;
};

export type ReportState = {
  comment: string;
  isCommentDirty: boolean;
};

export type StudySaveState = {
  _id: string;
  patient?: Patient;
  report?: ReportState;
};

export type ViewerStudy = Study & {
  images: ViewerImage[];
  saveState?: StudySaveState;
  defaultHasAntiScatteringGrid?: boolean;
};

export type ViewerImages = {
  [imageId: string]: ViewerImage;
};
